import React, { useEffect, useMemo, useState } from 'react';
import { admin } from '../services/api';
import http from '../services/api';
import { toast } from '../hooks/use-toast';
import { Users, Search, Loader2, Key, Shield, Store, User, X, Copy, RefreshCw, Save, EyeOff, Eye, Trash2, AlertTriangle, Info, Phone, Mail, Calendar, IdCard, Download } from 'lucide-react';

const ROLE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'admin', label: 'Admins' },
  { key: 'owner', label: 'Business owners' },
  { key: 'member', label: 'Members' },
];

// 12-char random password: A-Z a-z 0-9 plus a symbol so it always meets the 6-char min + is safe.
function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const symbols = '!@#$%&*';
  let out = '';
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  out += symbols[Math.floor(Math.random() * symbols.length)];
  out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function AdminUsersPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // password modal
  const [detailsUser, setDetailsUser] = useState(null); // signup details modal
  const [confirmAction, setConfirmAction] = useState(null); // { user, action: 'hide'|'unhide'|'delete', typed?: string }
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(() => new Set()); // user ids checked for bulk delete
  const [bulkConfirm, setBulkConfirm] = useState(null); // { typed, progress? }

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (role !== 'all') params.role = role;
      if (category !== 'all') params.category = category;
      const data = await admin.listUsers(params);
      setRows(data);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, category]);
  useEffect(() => {
    // One-shot category dropdown — not reloaded with the user list.
    http.get('/admin/business-categories')
      .then(r => setCategories(r.data || []))
      .catch(() => setCategories([]));
  }, []);

  const submitPassword = async () => {
    if (!modal) return;
    if (!modal.pw || modal.pw.length < 6) {
      toast({ title: 'Password too short', description: 'Minimum 6 characters.' });
      return;
    }
    setBusy('pw:submit');
    try {
      await admin.setUserPassword(modal.user.id, modal.pw);
      toast({ title: 'Password updated', description: `${modal.user.email} — takes effect immediately.` });
      // Keep the modal open so admin can copy the new password to share with the owner.
      setModal({ ...modal, saved: true });
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(null); }
  };

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast({ title: 'Copied' }); }
    catch { toast({ title: 'Copy failed — please copy manually' }); }
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { user: u, action } = confirmAction;
    setBusy(`${action}:${u.id}`);
    try {
      if (action === 'hide') {
        await admin.hideUser(u.id);
        setRows(rs => rs.map(r => r.id === u.id ? { ...r, isHidden: true } : r));
        toast({ title: 'Account hidden', description: `${u.email} — vanished from public listings.` });
      } else if (action === 'unhide') {
        await admin.unhideUser(u.id);
        setRows(rs => rs.map(r => r.id === u.id ? { ...r, isHidden: false } : r));
        toast({ title: 'Account restored', description: `${u.email} — visible again.` });
      } else if (action === 'delete') {
        const r = await admin.deleteUser(u.id);
        setRows(rs => rs.filter(x => x.id !== u.id));
        toast({ title: 'Account deleted', description: `Removed ${u.email} and ${r?.cascadedBusinesses || 0} business(es).` });
      }
      setConfirmAction(null);
    } catch (err) {
      toast({ title: 'Action failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(null); }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter(u =>
      (u.email || '').toLowerCase().includes(needle) ||
      (u.name || '').toLowerCase().includes(needle) ||
      (u.id || '').toLowerCase().includes(needle));
  }, [rows, q]);

  // Only non-admin accounts are eligible for bulk delete (same rule as single-row delete).
  const bulkEligible = useMemo(() => filtered.filter(u => !u.isAdmin), [filtered]);
  const bulkEligibleIds = useMemo(() => new Set(bulkEligible.map(u => u.id)), [bulkEligible]);
  const selectedVisibleCount = useMemo(
    () => bulkEligible.filter(u => selected.has(u.id)).length,
    [bulkEligible, selected],
  );
  const allVisibleSelected = bulkEligible.length > 0 && selectedVisibleCount === bulkEligible.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  // Drop ids that are no longer visible (filter/search change) so the counter stays truthful.
  useEffect(() => {
    setSelected(prev => {
      let changed = false;
      const next = new Set();
      prev.forEach(id => {
        if (bulkEligibleIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [bulkEligibleIds]);

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(prev => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        bulkEligible.forEach(u => next.delete(u.id));
        return next;
      }
      const next = new Set(prev);
      bulkEligible.forEach(u => next.add(u.id));
      return next;
    });
  };

  const runBulkDelete = async () => {
    if (!bulkConfirm) return;
    const ids = Array.from(selected).filter(id => bulkEligibleIds.has(id));
    if (!ids.length) { setBulkConfirm(null); return; }
    setBusy('bulk-delete');
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await admin.deleteUser(id);
        ok += 1;
        setRows(rs => rs.filter(x => x.id !== id));
        setBulkConfirm(bc => bc ? { ...bc, progress: { done: ok + fail, total: ids.length } } : bc);
      } catch {
        fail += 1;
      }
    }
    setSelected(new Set());
    setBulkConfirm(null);
    setBusy(null);
    toast({
      title: fail ? `Deleted ${ok} of ${ids.length}` : `Deleted ${ok} account${ok === 1 ? '' : 's'}`,
      description: fail ? `${fail} failed — retry the remaining rows.` : 'All selected accounts removed.',
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Search by email, name, or user id…"
            data-testid="admin-users-search"
            className="w-full bg-black border border-[#1f1f1f] rounded-lg pl-9 pr-3 py-2 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1">
          {loading ? <Loader2 size={12} className="animate-spin"/> : <Search size={12}/>} Search
        </button>
        <ExportButton q={q} role={role} category={category} />
        <div className="text-xs text-gray-500 ml-auto">{filtered.length} accounts</div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {ROLE_FILTERS.map(r => (
          <button key={r.key} onClick={() => setRole(r.key)}
            data-testid={`admin-users-role-${r.key}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${role === r.key ? 'bg-green-500 text-black border-green-500' : 'border-[#2a2a2a] text-gray-300 hover:border-green-500/50'}`}>
            {r.label}
          </button>
        ))}
        <div className="h-5 w-px bg-neutral-800 mx-1"/>
        <label className="text-[10px] uppercase tracking-wider text-gray-500">Listing category</label>
        <select value={category} onChange={e => setCategory(e.target.value)}
          data-testid="admin-users-category-filter"
          className="bg-black border border-[#2a2a2a] rounded-full text-xs font-semibold text-gray-200 px-3 py-1 hover:border-green-500/50 focus:outline-none focus:ring-2 focus:ring-green-500/40">
          <option value="all">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {category !== 'all' && (
          <button onClick={() => setCategory('all')} data-testid="admin-users-category-clear"
            className="text-[10px] text-gray-500 hover:text-white underline">
            Clear
          </button>
        )}
      </div>

      {selected.size > 0 && (
        <div
          data-testid="admin-users-bulk-bar"
          className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm"
        >
          <div className="text-red-100">
            <b data-testid="admin-users-bulk-count">{selected.size}</b> selected ·{' '}
            <span className="text-red-200/80 text-xs">Admins are excluded from bulk delete.</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-users-bulk-clear"
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-100 hover:bg-red-500/10 text-xs font-semibold"
            >
              Clear
            </button>
            <button
              data-testid="admin-users-bulk-delete"
              onClick={() => setBulkConfirm({ typed: '' })}
              disabled={busy === 'bulk-delete'}
              className="px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
            >
              <Trash2 size={12}/> Delete Selected
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading users…</div>
      ) : filtered.length === 0 ? (
        <div className="pbb-card p-10 text-center">
          <Users className="mx-auto text-green-400" size={26}/>
          <div className="mt-3 font-semibold">No accounts match</div>
        </div>
      ) : (
        <div className="pbb-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0d1f14] text-left text-xs text-gray-400 uppercase">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    data-testid="admin-users-select-all"
                    aria-label="Select all members on this page"
                    disabled={bulkEligible.length === 0}
                    checked={allVisibleSelected}
                    ref={el => { if (el) el.indeterminate = someVisibleSelected; }}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 accent-red-500 cursor-pointer disabled:opacity-40"
                  />
                </th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Businesses</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3">Password updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}
                  onClick={(e) => {
                    // Open details on body-click; ignore clicks on inputs/buttons/links.
                    const tag = (e.target.tagName || '').toLowerCase();
                    if (tag === 'input' || tag === 'button' || tag === 'a' || tag === 'select' || e.target.closest('button, a, input, select')) return;
                    setDetailsUser(u);
                  }}
                  className="border-t border-[#1f1f1f] cursor-pointer hover:bg-neutral-900/40 transition-colors">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      data-testid={`admin-user-select-${u.id}`}
                      aria-label={`Select ${u.email}`}
                      disabled={u.isAdmin}
                      title={u.isAdmin ? 'Admins cannot be bulk-deleted' : 'Select for bulk delete'}
                      checked={selected.has(u.id)}
                      onChange={() => toggleRow(u.id)}
                      className="h-4 w-4 accent-red-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white flex items-center gap-2">
                      {u.name || '(no name)'}
                      {u.isHidden && (
                        <span data-testid={`admin-user-hidden-badge-${u.id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-gray-500/40 text-gray-300 bg-gray-500/10 inline-flex items-center gap-1">
                          <EyeOff size={10}/> Hidden
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                    <div className="text-[10px] text-gray-600">{u.id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-300 flex items-center gap-1" data-testid={`admin-user-phone-${u.id}`}>
                      <Phone size={11} className="text-gray-500"/> {u.phoneDisplay || u.phone || <span className="text-gray-600 italic">—</span>}
                    </div>
                    <div className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5">
                      <Mail size={10}/> {u.email}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {u.isAdmin && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-red-500/40 text-red-300 bg-red-500/10 inline-flex items-center gap-1 w-fit"><Shield size={10}/> Admin</span>}
                      {u.isBusinessOwner && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-yellow-500/40 text-yellow-300 bg-yellow-500/10 inline-flex items-center gap-1 w-fit"><Store size={10}/> Owner</span>}
                      {!u.isAdmin && !u.isBusinessOwner && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-gray-500/40 text-gray-300 bg-gray-500/10 inline-flex items-center gap-1 w-fit"><User size={10}/> Member</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    <div data-testid={`admin-user-biz-count-${u.id}`}>{u.businessCount || 0}</div>
                    {Array.isArray(u.businessCategories) && u.businessCategories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1 max-w-[220px]" data-testid={`admin-user-biz-cats-${u.id}`}>
                        {u.businessCategories.slice(0, 3).map((c, i) => (
                          <span key={i} className="text-[9px] bg-green-500/10 border border-green-500/30 text-green-200 rounded px-1.5 py-0.5 font-semibold">{c}</span>
                        ))}
                        {u.businessCategories.length > 3 && (
                          <span className="text-[9px] text-gray-500">+{u.businessCategories.length - 3}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">{u.passwordUpdatedAt ? new Date(u.passwordUpdatedAt).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <button data-testid={`admin-user-details-${u.id}`}
                        onClick={() => setDetailsUser(u)}
                        className="px-2.5 py-1.5 rounded-lg bg-neutral-700 hover:bg-neutral-600 text-white text-xs font-semibold inline-flex items-center gap-1">
                        <Eye size={12}/> View
                      </button>
                      <button data-testid={`admin-user-pw-${u.id}`}
                        onClick={() => setModal({ user: u, pw: generatePassword(), saved: false })}
                        className="px-2.5 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-xs font-semibold inline-flex items-center gap-1">
                        <Key size={12}/> Password
                      </button>
                      {u.isHidden ? (
                        <button data-testid={`admin-user-unhide-${u.id}`}
                          onClick={() => setConfirmAction({ user: u, action: 'unhide' })}
                          className="px-2.5 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold inline-flex items-center gap-1">
                          <Eye size={12}/> Unhide
                        </button>
                      ) : (
                        <button data-testid={`admin-user-hide-${u.id}`}
                          onClick={() => setConfirmAction({ user: u, action: 'hide' })}
                          className="px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold inline-flex items-center gap-1">
                          <EyeOff size={12}/> Hide
                        </button>
                      )}
                      <button data-testid={`admin-user-delete-${u.id}`}
                        onClick={() => setConfirmAction({ user: u, action: 'delete', typed: '' })}
                        disabled={u.isAdmin}
                        title={u.isAdmin ? 'Demote admin first' : 'Permanently delete this account'}
                        className="px-2.5 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                        <Trash2 size={12}/> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailsUser && (
        <UserDetailsModal user={detailsUser} onClose={() => setDetailsUser(null)} />
      )}

      {modal && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-blue-500/30 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold flex items-center gap-2"><Key size={16} className="text-blue-400"/> Set password</div>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Account</div>
                <div className="text-white">{modal.user.name || '(no name)'}</div>
                <div className="text-xs text-gray-500">{modal.user.email}</div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">New password</label>
                <div className="flex gap-2 mt-1">
                  <input data-testid="admin-pw-input" type="text" value={modal.pw}
                    onChange={e => setModal({ ...modal, pw: e.target.value, saved: false })}
                    className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm font-mono"/>
                  <button onClick={() => setModal({ ...modal, pw: generatePassword(), saved: false })}
                    data-testid="admin-pw-regen"
                    className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-blue-500 text-blue-300 text-xs inline-flex items-center gap-1">
                    <RefreshCw size={12}/> Random
                  </button>
                </div>
                <div className="text-[11px] text-gray-500 mt-1">Minimum 6 chars. Effective immediately — any active reset link is invalidated.</div>
              </div>
              {modal.saved && (
                <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-3 text-xs text-green-200" data-testid="admin-pw-saved">
                  <div className="font-semibold text-green-300">Password updated ✓</div>
                  <div className="mt-1">Share this with <b>{modal.user.email}</b>:</div>
                  <div className="mt-2 flex items-center gap-2 bg-black/40 border border-green-500/20 rounded px-2 py-1.5 font-mono">
                    <span className="flex-1 break-all">{modal.pw}</span>
                    <button onClick={() => copy(modal.pw)} className="text-green-300 hover:text-green-200"><Copy size={14}/></button>
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 text-sm">
                {modal.saved ? 'Done' : 'Cancel'}
              </button>
              {!modal.saved && (
                <button data-testid="admin-pw-submit" onClick={submitPassword} disabled={busy === 'pw:submit'}
                  className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1">
                  {busy === 'pw:submit' ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Set password
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <ConfirmActionModal
          state={confirmAction}
          onCancel={() => setConfirmAction(null)}
          onConfirm={runConfirmedAction}
          busy={busy === `${confirmAction.action}:${confirmAction.user.id}`}
          onChange={(patch) => setConfirmAction({ ...confirmAction, ...patch })}
        />
      )}

      {bulkConfirm && (
        <BulkDeleteConfirmModal
          state={bulkConfirm}
          count={selected.size}
          busy={busy === 'bulk-delete'}
          onCancel={() => busy === 'bulk-delete' ? null : setBulkConfirm(null)}
          onConfirm={runBulkDelete}
          onChange={(patch) => setBulkConfirm({ ...bulkConfirm, ...patch })}
        />
      )}
    </div>
  );
}

function ConfirmActionModal({ state, onCancel, onConfirm, busy, onChange }) {
  const { user: u, action } = state;
  const isDelete = action === 'delete';
  const isHide = action === 'hide';
  const isUnhide = action === 'unhide';
  const requiredWord = isDelete ? 'DELETE' : null;
  const typedOk = !requiredWord || (state.typed || '').trim().toUpperCase() === requiredWord;
  const tone = isDelete ? 'red' : isHide ? 'amber' : 'emerald';
  const toneClasses = {
    red: { ring: 'border-red-500/40', icon: 'text-red-400', btn: 'bg-red-500 hover:bg-red-400 text-white', title: 'Permanently delete this account?' },
    amber: { ring: 'border-amber-500/40', icon: 'text-amber-400', btn: 'bg-amber-500 hover:bg-amber-400 text-black', title: 'Hide this account?' },
    emerald: { ring: 'border-emerald-500/40', icon: 'text-emerald-400', btn: 'bg-emerald-500 hover:bg-emerald-400 text-black', title: 'Restore this account?' },
  }[tone];

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid={`admin-user-confirm-${action}`}>
      <div className={`w-full max-w-md bg-[#0f0f0f] border ${toneClasses.ring} rounded-2xl overflow-hidden`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <div className="font-semibold flex items-center gap-2">
            <AlertTriangle size={16} className={toneClasses.icon}/>
            {toneClasses.title}
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-white"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Account</div>
            <div className="text-white">{u.name || '(no name)'}</div>
            <div className="text-xs text-gray-500">{u.email}</div>
            <div className="text-[10px] text-gray-600 mt-1">{u.id}</div>
          </div>
          {isDelete && (
            <>
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-200">
                This <b>permanently</b> removes the account. Their {u.businessCount || 0} business listing{u.businessCount === 1 ? '' : 's'} will be soft-deleted so they vanish from every public page. <b>This can't be undone.</b>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Type <span className="text-red-300 font-mono">DELETE</span> to confirm</label>
                <input
                  data-testid="admin-user-delete-confirm-input"
                  autoFocus
                  value={state.typed || ''}
                  onChange={e => onChange({ typed: e.target.value })}
                  className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm font-mono"
                  placeholder="DELETE"
                />
              </div>
            </>
          )}
          {isHide && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
              The account stays in the database — their profile and any businesses they own vanish from every public listing. You can unhide anytime.
            </div>
          )}
          {isUnhide && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-200">
              This restores the account and any businesses they own to public listings immediately.
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 text-sm">Cancel</button>
          <button
            data-testid={`admin-user-confirm-${action}-btn`}
            onClick={onConfirm}
            disabled={busy || !typedOk}
            className={`px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1 ${toneClasses.btn}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin"/> :
              isDelete ? <Trash2 size={12}/> : isHide ? <EyeOff size={12}/> : <Eye size={12}/>}
            {isDelete ? 'Delete forever' : isHide ? 'Hide account' : 'Restore account'}
          </button>
        </div>
      </div>
    </div>
  );
}


function BulkDeleteConfirmModal({ state, count, busy, onCancel, onConfirm, onChange }) {
  const typedOk = (state.typed || '').trim().toUpperCase() === 'DELETE';
  const progress = state.progress;
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid="admin-users-bulk-confirm">
      <div className="w-full max-w-md bg-[#0f0f0f] border border-red-500/40 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <div className="font-semibold flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-400"/>
            Permanently delete {count} account{count === 1 ? '' : 's'}?
          </div>
          <button onClick={onCancel} disabled={busy} className="text-gray-400 hover:text-white disabled:opacity-40"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-200">
            This <b>permanently</b> removes <b>{count}</b> selected account{count === 1 ? '' : 's'} and soft-deletes any business listings they own so they vanish from every public page. <b>This can't be undone.</b>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
              Type <span className="text-red-300 font-mono">DELETE</span> to confirm
            </label>
            <input
              data-testid="admin-users-bulk-confirm-input"
              autoFocus
              disabled={busy}
              value={state.typed || ''}
              onChange={e => onChange({ typed: e.target.value })}
              className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm font-mono disabled:opacity-50"
              placeholder="DELETE"
            />
          </div>
          {progress && (
            <div className="text-xs text-gray-400" data-testid="admin-users-bulk-progress">
              Deleting {progress.done} of {progress.total}…
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 text-sm disabled:opacity-40">Cancel</button>
          <button
            data-testid="admin-users-bulk-confirm-btn"
            onClick={onConfirm}
            disabled={busy || !typedOk || count === 0}
            className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1"
          >
            {busy ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
            Delete {count} forever
          </button>
        </div>
      </div>
    </div>
  );
}

// -------- Signup details modal (admin-only) --------------------------------
// Shows every field we captured when the user signed up: name, email, phone,
// avatar, join date, last active, role, business ownership, loyalty tier,
// marketing opt-ins, interests from the onboarding survey, referral code.
function UserDetailsModal({ user, onClose }) {
  if (!user) return null;
  const copy = (txt) => { if (!txt) return; navigator.clipboard?.writeText(String(txt)).catch(() => {}); };
  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  const survey = user.onboardingSurvey || {};
  const interests = Array.isArray(survey.interests) ? survey.interests : [];
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
         data-testid="admin-user-details-modal">
      <div className="w-full max-w-xl bg-[#0f0f0f] border border-neutral-700 rounded-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f] bg-gradient-to-r from-neutral-900 to-black">
          <div className="flex items-center gap-3">
            {user.avatar ? (
              <img src={user.avatar} alt="" className="w-10 h-10 rounded-full object-cover border border-neutral-700"/>
            ) : (
              <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-400"><User size={18}/></div>
            )}
            <div>
              <div className="font-bold text-white">{user.name || '(no name)'}</div>
              <div className="text-xs text-gray-500">Sign-up details</div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="admin-user-details-close">
            <X size={18}/>
          </button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4 text-sm">
          <Section title="Contact">
            <Row icon={User} label="Full name" value={user.name} copyable onCopy={() => copy(user.name)}/>
            <Row icon={Mail} label="Email" value={user.email} copyable onCopy={() => copy(user.email)} testId="details-email"/>
            <Row icon={Phone} label="Phone" value={user.phoneDisplay || user.phone} copyable onCopy={() => copy(user.phoneDisplay || user.phone)} testId="details-phone"/>
          </Section>

          <Section title="Account">
            <Row icon={IdCard} label="User ID" value={user.id} copyable onCopy={() => copy(user.id)} mono/>
            <Row icon={Calendar} label="Joined" value={fmt(user.createdAt)}/>
            <Row icon={Calendar} label="Last active" value={fmt(user.lastActiveAt)}/>
            <Row icon={Calendar} label="Password last updated" value={fmt(user.passwordUpdatedAt)}/>
            <Row icon={Shield} label="Role" value={
              <span className="inline-flex flex-wrap gap-1">
                {user.isAdmin && <RoleBadge color="red" icon={Shield} label="Admin"/>}
                {user.isBusinessOwner && <RoleBadge color="yellow" icon={Store} label="Business owner"/>}
                {!user.isAdmin && !user.isBusinessOwner && <RoleBadge color="gray" icon={User} label="Member"/>}
                {user.isAffiliate && <RoleBadge color="green" icon={Users} label="Affiliate"/>}
                {user.isHidden && <RoleBadge color="gray" icon={EyeOff} label="Hidden"/>}
              </span>
            }/>
            {user.referralCode && <Row icon={IdCard} label="Referral code" value={user.referralCode} copyable onCopy={() => copy(user.referralCode)} mono/>}
          </Section>

          <Section title="Loyalty">
            <Row label="Tier" value={user.tier || '—'}/>
            <Row label="Points" value={typeof user.points === 'number' ? user.points.toLocaleString() : '—'}/>
            <Row label="Member since" value={fmt(user.memberSince)}/>
            <Row label="Login count" value={typeof user.loginCount === 'number' ? user.loginCount.toLocaleString() : '—'}/>
          </Section>

          {(survey.interests?.length || survey.city || survey.howHeard) && (
            <Section title="Onboarding survey">
              {survey.city && <Row label="City" value={survey.city}/>}
              {survey.howHeard && <Row label="How they heard" value={survey.howHeard}/>}
              {interests.length > 0 && (
                <Row label="Interests" value={
                  <span className="inline-flex flex-wrap gap-1">
                    {interests.map((t, i) => (
                      <span key={i} className="text-[10px] bg-neutral-800 border border-neutral-700 rounded px-1.5 py-0.5">{t}</span>
                    ))}
                  </span>
                }/>
              )}
            </Section>
          )}

          <Section title="Marketing">
            <Row label="Subscribed to deals" value={user.subscribed ? 'Yes' : 'No'}/>
            <Row label="SMS opt-out" value={user.smsOptOut ? 'Yes' : 'No'}/>
            <Row label="Businesses owned" value={user.businessCount || 0}/>
            {Array.isArray(user.businessCategories) && user.businessCategories.length > 0 && (
              <Row label="Listing categories" testId="details-business-categories" value={
                <span className="inline-flex flex-wrap gap-1">
                  {user.businessCategories.map((c, i) => (
                    <span key={i} className="text-[10px] bg-green-500/10 border border-green-500/30 text-green-200 rounded px-1.5 py-0.5 font-semibold">{c}</span>
                  ))}
                </span>
              }/>
            )}
          </Section>
        </div>
        <div className="px-5 py-3 border-t border-[#1f1f1f] text-right">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-semibold">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{title}</div>
      <div className="space-y-1.5 bg-black/30 border border-[#1a1a1a] rounded-lg p-3">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value, copyable, onCopy, mono, testId }) {
  const empty = value == null || value === '' || value === '—';
  return (
    <div className="flex items-start gap-2 text-xs" data-testid={testId}>
      {Icon && <Icon size={12} className="text-gray-500 mt-1 shrink-0"/>}
      <div className="w-32 text-gray-500 shrink-0">{label}</div>
      <div className={`flex-1 ${mono ? 'font-mono text-[11px]' : ''} ${empty ? 'text-gray-600 italic' : 'text-white'} break-all`}>
        {empty ? '—' : value}
      </div>
      {copyable && !empty && (
        <button onClick={onCopy} className="text-gray-500 hover:text-white shrink-0" title="Copy">
          <Copy size={11}/>
        </button>
      )}
    </div>
  );
}

function RoleBadge({ color, icon: Icon, label }) {
  const colorMap = {
    red: 'border-red-500/40 text-red-300 bg-red-500/10',
    yellow: 'border-yellow-500/40 text-yellow-300 bg-yellow-500/10',
    green: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10',
    gray: 'border-gray-500/40 text-gray-300 bg-gray-500/10',
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border inline-flex items-center gap-1 ${colorMap[color] || colorMap.gray}`}>
      <Icon size={10}/> {label}
    </span>
  );
}


// -------- Export button (XLSX / CSV via /api/admin/users/export) -----------
function ExportButton({ q, role, category }) {
  const [busy, setBusy] = useState(null);
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const params = { fmt };
      if (q) params.q = q;
      if (role && role !== 'all') params.role = role;
      if (category && category !== 'all') params.category = category;
      const resp = await http.get('/admin/users/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      // Pull filename from Content-Disposition when present.
      const cd = resp.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `pbb-users.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: 'Export ready', description: `Downloaded ${a.download}` });
    } catch (err) {
      toast({ title: 'Export failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="inline-flex rounded-lg overflow-hidden border border-neutral-700" data-testid="admin-users-export">
      <button onClick={() => download('xlsx')} disabled={!!busy}
        data-testid="admin-users-export-xlsx"
        title="Download as Excel (.xlsx)"
        className="px-3 py-2 bg-neutral-800 hover:bg-neutral-700 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
        {busy === 'xlsx' ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} Excel
      </button>
      <button onClick={() => download('csv')} disabled={!!busy}
        data-testid="admin-users-export-csv"
        title="Download as CSV"
        className="px-3 py-2 bg-neutral-900 hover:bg-neutral-800 border-l border-neutral-700 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
        {busy === 'csv' ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} CSV
      </button>
    </div>
  );
}

